import React, { useEffect, useState } from "react";
import styles from "./CardStyles.module.css";
import { fetchHomePageData, type HomePageMentor } from "../fetchHomePageData";
import type { MentorPanel } from "../../shared/mentorTypes";
import { useNavigate } from "react-router-dom";
import useTenant from "../../tenancy/useTenantPath";
import { mentorPanelClickEventBuilder, sendEvent } from "../../utils/event-helpers";
import { useAppDispatch, useAppSelector } from "../../app/store";

interface PanelCardProps {
  mentorPanel: MentorPanel;
  dataCy?: string;
}

const PanelCard = ({ mentorPanel, dataCy }: PanelCardProps) => {
  const { title, subtitle, mentors } = mentorPanel;
  const displayMentors = mentors.slice(0, 4);
  const dispatch = useAppDispatch();

  const { userId, sessionId } = useAppSelector((state) => state.session);

  // State to store the fetched mentor data
  const [allMentorData, setAllMentorData] = useState<HomePageMentor[]>([]);

  const navigate = useNavigate();
  const tenant = useTenant();

  // Fetch home page data
  const data = async () =>
    await fetchHomePageData(import.meta.env.VITE_PATHWAYS_ORG_ID);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const homePageData = await data();
        setAllMentorData(homePageData.mentors); // Store mentor data in state
      } catch (error) {
        console.error("Failed to fetch home page data:", error);
      }
    };

    void fetchData();
  }, []);

  const findMentorById = (mentorId: string): HomePageMentor | undefined => {
    return allMentorData.find((each) => each._id === mentorId);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target;

    if (!(target instanceof Element)) return;

    if (!target.closest(".card-bookmark-button")) {
      navigate(tenant.basePath + "/mentorpal?panelId=" + mentorPanel._id);
    }

    if (!userId || !sessionId) {
      return;
    }
    
    const eventData = mentorPanelClickEventBuilder(
      userId,
      sessionId,
      mentorPanel,
      displayMentors.map((m) => findMentorById(m)?.name ?? ""),
      {},
    );
    void dispatch(sendEvent(eventData));
  };

  return (
    <div className={styles.card} onClick={handleClick} data-cy={dataCy}>
      <div className={styles.cardContent}>
        <h2 className={styles.cardTitle}>{title}</h2>
        <div className={styles.panelCardLink}>
          <div className={styles.panelCardThumbnailContainer}>
            {displayMentors.map((mentor, index) => {
              const mentorData = findMentorById(mentor);
              return (
                mentorData && (
                  <React.Fragment key={index}>
                    <img
                      src={mentorData.thumbnail}
                      alt={mentorData.name}
                      className={styles.panelCardThumbnail}
                    />
                  </React.Fragment>
                )
              );
            })}
          </div>
        </div>
        <h3 className={styles.cardSubtitle}>{subtitle}</h3>
      </div>
    </div>
  );
};

export default PanelCard;
